import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { Camera, Check, Loader2, Mail, KeyRound, ShieldCheck, ShieldAlert, Upload, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import OnboardingTour from "@/components/OnboardingTour";
import AdminTools from "@/components/AdminTools";
import Avatar from "@/pages/profile/Avatar";
import usePushNotifications from "@/hooks/usePushNotifications";
import { enablePush, disablePush, sendTestPush } from "@/lib/push";

export default function Profile() {
  const { user, updateProfile, formatApiError: fmt } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [avatars, setAvatars] = useState([]);
  const [savingProfile, setSavingProfile] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwLoading, setPwLoading] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [resending, setResending] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  // Push notifications (browser web push) — v27.14
  const { state: pushState, busy: pushBusy, setBusy: setPushBusy, refresh: refreshPushState } = usePushNotifications();
  const fileRef = useRef(null);

  const loadAvatars = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/avatars");
      setAvatars(data.avatars || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadAvatars(); }, [loadAvatars]);
  useEffect(() => { setName(user?.name || ""); }, [user?.name]);

  const saveName = async () => {
    if (!name.trim() || name === user?.name) return;
    setSavingProfile(true);
    try {
      await updateProfile({ name: name.trim() });
      toast.success("Name updated");
    } catch (e) { toast.error(fmt(e.response?.data?.detail)); }
    finally { setSavingProfile(false); }
  };

  const pickAvatar = async (url) => {
    try {
      await updateProfile({ avatar_url: url });
      toast.success("Avatar updated");
    } catch (e) { toast.error(fmt(e.response?.data?.detail)); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/auth/me/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await updateProfile({ avatar_url: data.avatar_url });
      toast.success("Photo uploaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploadingAvatar(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.next.length < 8) return toast.error("New password must be at least 8 characters.");
    if (pw.next !== pw.confirm) return toast.error("New passwords don't match.");
    setPwLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      setPw({ current: "", next: "", confirm: "" });
      toast.success("Password changed");
    } catch (e) { toast.error(fmt(e.response?.data?.detail)); }
    finally { setPwLoading(false); }
  };
  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushState.subscribed) {
        await disablePush();
        toast.success("Notifications turned off.");
      } else {
        const res = await enablePush();
        toast.success(
          res.mocked
            ? "Notifications enabled — running in preview mode (test only)."
            : "Notifications enabled.",
        );
      }
      await refreshPushState();
    } catch (e) {
      toast.error(e.message || "Couldn't change notification setting.");
    } finally { setPushBusy(false); }
  };

  const testPush = async () => {
    setPushBusy(true);
    try {
      const res = await sendTestPush();
      if (res.sent > 0) toast.success("Test notification sent — check your device.");
      else if (res.mocked > 0) toast.info(`Preview mode — ${res.mocked} test push logged to server (VAPID keys not configured yet).`);
      else toast.info("No devices subscribed yet.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Test failed.");
    } finally { setPushBusy(false); }
  };


  // Group presets by region for a curated, less-overwhelming picker.
  const groupedAvatars = React.useMemo(() => {
    const out = [];
    const seen = new Map();
    for (const a of avatars) {
      const key = a.region_label || "Other";
      if (!seen.has(key)) { seen.set(key, out.length); out.push({ label: key, items: [] }); }
      out[seen.get(key)].items.push(a);
    }
    return out;
  }, [avatars]);

  const resendVerification = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent — check your inbox.");
    } catch (e) { toast.error(fmt(e.response?.data?.detail)); }
    finally { setResending(false); }
  };

  if (!user) return null;

  return (
    <div>
      <PageHeader
        eyebrow="Profile"
        title="You."
        subtitle="A quiet corner to shape how you appear to the rest of the household."
      />

      {/* Identity card */}
      <section className="warm-card p-6 mb-6" data-testid="profile-identity-card">
        <div className="flex items-center gap-5 flex-wrap">
          <Avatar url={user.avatar_url} name={user.name} size={96} />
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-display text-3xl text-hearth-ink leading-none" data-testid="profile-name">{user.name}</h3>
              {user.email_verified ? (
                <span className="chip bg-hearth-sage/15 text-hearth-sageDark inline-flex items-center gap-1" data-testid="profile-verified-chip">
                  <ShieldCheck size={11} /> verified
                </span>
              ) : (
                <span className="chip bg-hearth-amber/20 text-[#7A5320] inline-flex items-center gap-1" data-testid="profile-unverified-chip">
                  <ShieldAlert size={11} /> unverified
                </span>
              )}
            </div>
            <p className="caption mt-1 inline-flex items-center gap-1.5"><Mail size={11}/> {user.email}</p>
            {!user.email_verified && (
              <button
                onClick={resendVerification}
                disabled={resending}
                data-testid="profile-resend-verification"
                className="mt-2 text-xs text-hearth-clayDark hover:text-hearth-clay underline-offset-2 hover:underline"
              >
                {resending ? "Sending…" : "Resend verification email"}
              </button>
            )}
          </div>
        </div>

        <div className="brass-hair mt-6" />

        <div className="mt-5 grid grid-cols-1 md:grid-cols-[1fr,auto] gap-3 items-end">
          <div>
            <Label className="caption text-hearth-mute">Name</Label>
            <Input
              data-testid="profile-name-input"
              value={name} onChange={(e) => setName(e.target.value)}
              className="input-warm mt-1.5 h-11" placeholder="Your name"
            />
          </div>
          <Button
            data-testid="profile-name-save"
            onClick={saveName}
            disabled={savingProfile || !name.trim() || name === user.name}
            className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white h-11 px-5"
          >
            {savingProfile ? <Loader2 size={14} className="animate-spin"/> : <><Check size={14} className="mr-1.5"/> Save</>}
          </Button>
        </div>
      </section>

      {/* Avatar library — grouped by region + on-demand generator */}
      <section className="warm-card p-6 mb-6" data-testid="profile-avatar-library">
        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
          <div>
            <h3 className="font-display text-2xl text-hearth-ink">Pick a face for your family album</h3>
            <p className="caption mt-0.5">
              A few gentle characters from every corner of the world — or upload
              your own photo.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <input
              type="file"
              ref={fileRef}
              accept="image/*"
              hidden
              onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value = ""; }}
            />
            <Button
              data-testid="profile-upload-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploadingAvatar}
              variant="ghost"
              className="rounded-full border border-hearth-line hover:bg-hearth-stone text-sm h-10"
            >
              {uploadingAvatar ? <Loader2 size={13} className="animate-spin mr-1.5"/> : <Upload size={13} className="mr-1.5"/>}
              Upload photo
            </Button>
          </div>
        </div>

        {/* Grouped preset avatars, one region at a time */}
        <div className="space-y-6" data-testid="profile-avatar-groups">
          {groupedAvatars.map((group) => (
            <div key={group.label} data-testid={`avatar-group-${group.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="caption text-hearth-mute mb-2 flex items-center gap-2">
                <span className="w-6 h-px bg-hearth-clay/30"/>
                <span className="text-hearth-clayDark tracking-widest">{group.label}</span>
                <span className="w-6 h-px bg-hearth-clay/30"/>
              </div>
              <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 gap-3">
                {group.items.map((a) => {
                  const active = user.avatar_url === a.url;
                  return (
                    <button
                      key={a.id}
                      onClick={() => pickAvatar(a.url)}
                      data-testid={`avatar-option-${a.id}`}
                      className={`relative rounded-full overflow-hidden border-2 transition ${
                        active
                          ? "border-hearth-clay shadow-md scale-[1.05]"
                          : "border-hearth-line hover:border-hearth-clayDark hover:scale-[1.05]"
                      }`}
                      style={{ width: 64, height: 64 }}
                      title={a.id}
                    >
                      <img src={a.url} alt={a.id} className="w-full h-full object-cover" />
                      {active && (
                        <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-hearth-clay text-white grid place-items-center border-2 border-hearth-paper">
                          <Check size={12} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Change password */}
      <section className="warm-card p-6 mb-6" data-testid="profile-password-card">
        <h3 className="font-display text-2xl text-hearth-ink inline-flex items-center gap-2">
          <KeyRound size={16} className="text-hearth-clay"/> Change password
        </h3>
        <p className="caption mt-0.5">A gentle refresh — at least 8 characters.</p>
        <form onSubmit={changePassword} className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="caption text-hearth-mute">Current password</Label>
            <Input
              data-testid="profile-current-password"
              type="password" required
              value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })}
              className="input-warm mt-1.5 h-11"
            />
          </div>
          <div>
            <Label className="caption text-hearth-mute">New password</Label>
            <Input
              data-testid="profile-new-password"
              type="password" required
              value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })}
              className="input-warm mt-1.5 h-11"
            />
          </div>
          <div>
            <Label className="caption text-hearth-mute">Confirm new</Label>
            <Input
              data-testid="profile-confirm-password"
              type="password" required
              value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              className="input-warm mt-1.5 h-11"
            />
          </div>
          <div className="md:col-span-3">
            <Button
              type="submit"
              data-testid="profile-change-password-btn"
              disabled={pwLoading || !pw.current || !pw.next}
              className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white h-11 px-5"
            >
              {pwLoading ? <Loader2 size={14} className="animate-spin"/> : "Change password"}
            </Button>
          </div>
        </form>
      </section>

      {/* Push notifications — v27.14 (mock-mode-safe: works even without VAPID keys) */}
      <section className="warm-card p-6 mb-6" data-testid="profile-push-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <h3 className="font-display text-2xl text-hearth-ink flex items-center gap-2">
              {pushState.subscribed ? <Bell size={18} className="text-hearth-clay"/> : <BellOff size={18} className="text-hearth-mute"/>}
              Browser notifications
            </h3>
            <p className="text-sm text-hearth-mute mt-1 max-w-md">
              {!pushState.supported
                ? "This browser doesn't support push notifications. Try Chrome or Safari on desktop or Android."
                : pushState.subscribed
                  ? "You'll get gentle pings for near-expiry pantry items and household activity."
                  : "Turn on notifications to know when a bill is due, food is about to spoil, or a household member sends a meal suggestion."}
              {pushState.supported && !pushState.hasKeys && (
                <span
                  data-testid="profile-push-preview-hint"
                  className="block mt-2 text-xs italic text-hearth-clay"
                >
                  Preview mode — notifications will be available once the app is deployed with VAPID keys configured.
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-col gap-2 items-stretch">
            <button
              data-testid="profile-push-toggle"
              onClick={togglePush}
              disabled={!pushState.supported || !pushState.hasKeys || pushBusy}
              title={!pushState.hasKeys && pushState.supported
                ? "Notifications will be available once VAPID keys are configured on the server."
                : undefined}
              className={`px-5 py-2.5 rounded-full text-sm transition ${
                pushState.subscribed
                  ? "bg-hearth-stone text-hearth-ink hover:bg-hearth-line disabled:opacity-40"
                  : "bg-hearth-clay text-white hover:bg-hearth-clayDark disabled:opacity-40 disabled:cursor-not-allowed"
              }`}
            >
              {pushBusy && <Loader2 size={13} className="inline mr-1.5 animate-spin"/>}
              {pushState.subscribed ? "Turn off" : "Enable notifications"}
            </button>
            {pushState.subscribed && (
              <button
                data-testid="profile-push-test"
                onClick={testPush}
                disabled={pushBusy}
                className="text-xs text-hearth-mute hover:text-hearth-ink underline underline-offset-2"
              >
                Send a test
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Replay setup — surfaces the First Light tour any time the user
          wants a refresher. Works whether they completed or skipped it. */}
      <section className="warm-card p-6 mb-6" data-testid="profile-setup-guide-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-display text-2xl text-hearth-ink">Setup guide</h3>
            <p className="text-sm text-hearth-mute mt-1 max-w-md">
              A gentle five-step walkthrough for how HearthHouse likes to
              be set up — cuisines, local shops, and the pantry drawer.
            </p>
          </div>
          <button
            data-testid="profile-replay-onboarding"
            onClick={() => setShowOnboarding(true)}
            className="px-5 py-2.5 rounded-full bg-hearth-stone text-hearth-ink text-sm hover:bg-hearth-line transition"
          >
            Replay First Light
          </button>
        </div>
      </section>

      {showOnboarding && (
        <OnboardingTour
          forceOpen
          onDone={() => setShowOnboarding(false)}
        />
      )}

      <AdminTools />

      <p className="text-center text-xs text-hearth-mute mt-6 space-x-3">
        <Link to="/privacy" data-testid="profile-privacy-link" className="text-hearth-clay underline hover:text-hearth-clayDark">
          How HearthHouse handles your data
        </Link>
        {user?.role === "owner" && (
          <>
            <span>·</span>
            <Link to="/status" data-testid="profile-status-link" className="text-hearth-clay underline hover:text-hearth-clayDark">
              Health status
            </Link>
          </>
        )}
      </p>
    </div>
  );
}
